import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { StorageManagerService } from '@app/shared/services/storage-manager.service';
import { DistributionSlice } from '../../core/models/dashboard.model';
import { DayBucket, TimeEntryMetricsModel } from '../../core/models/time-entry-metrics.model';
import { DistributionBarComponent } from '../distribution-bar/distribution-bar.component';
import { MetricCardComponent } from '../metric-card/metric-card.component';

type MetricsTab = 'visao' | 'distribuicao' | 'chamados' | 'qualidade';

/** Registrada em `storage-key-registry.ts` (HIGH/90d); sem registro seria tratada como LOW/30d. */
const HIDE_WEEKENDS_KEY = 'ticket_log_hide_weekends';

/**
 * Grade de métricas de apontamentos. Recebe o modelo já calculado e só o desenha — se um bloco
 * parecer exigir conta, falta campo no modelo: a conta é do motor
 * (`time-entry-metrics.builder.ts`), que é testável isoladamente.
 *
 * A faixa de contexto fica FORA das abas de propósito: ela diz de que recorte os números falam,
 * e escondida atrás de uma aba as outras três afirmariam totais sem dizer de quê.
 *
 * Gráficos em `div` + CSS, nunca `p-chart` — ver `ticket-dashboard.component.ts` e o how-to §6.
 */
@Component({
  selector: 'app-time-entry-metrics',
  standalone: true,
  imports: [
    FormsModule,
    TabsModule,
    ToggleSwitchModule,
    MetricCardComponent,
    DistributionBarComponent,
  ],
  templateUrl: './time-entry-metrics.component.html',
  styleUrls: ['./time-entry-metrics.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimeEntryMetricsComponent {
  readonly model = input.required<TimeEntryMetricsModel>();

  readonly sliceActivate = output<DistributionSlice>();

  /** Aba visível. Estado local: reabrir o modal volta para a visão geral. */
  readonly activeTab = signal<MetricsTab>('visao');

  private readonly storage = inject(StorageManagerService);

  /**
   * Filtra só a EXIBIÇÃO: a escala das barras vem do motor e cobre o período inteiro, então
   * esconder o fim de semana não faz o maior dia útil parecer maior do que é.
   */
  readonly esconderFimDeSemana = signal(this.carregarEsconderFimDeSemana());

  readonly porDiaExibido = computed<DayBucket[]>(() => {
    const dias = this.model().porDia;
    return this.esconderFimDeSemana() ? dias.filter(d => !d.isFimDeSemana) : dias;
  });

  onSliceActivate(slice: DistributionSlice): void {
    if (slice.target) {
      this.sliceActivate.emit(slice);
    }
  }

  onEsconderFimDeSemanaChange(valor: boolean): void {
    this.esconderFimDeSemana.set(valor);
    try {
      this.storage.setItem(HIDE_WEEKENDS_KEY, valor ? '1' : '0');
    } catch {
      /* preferência de exibição: falhar em salvar não pode quebrar o gráfico */
    }
  }

  private carregarEsconderFimDeSemana(): boolean {
    try {
      // `'1'` explícito, não truthy: valor ausente, expirado ou legacy cai no
      // default de mostrar a semana inteira.
      return this.storage.getItem(HIDE_WEEKENDS_KEY) === '1';
    } catch {
      return false;
    }
  }
}
